// Optimized Box-Muller transform with cached second value
let boxMullerSpare = null;
let hasSpare = false;

function boxMullerTransform() {
    if (hasSpare) {
        hasSpare = false;
        return boxMullerSpare;
    }
    
    hasSpare = true;
    let u = 0, v = 0;
    while(u === 0) u = Math.random();
    while(v === 0) v = Math.random();
    
    const mag = Math.sqrt(-2.0 * Math.log(u));
    const phase = 2.0 * Math.PI * v;
    
    boxMullerSpare = mag * Math.sin(phase);
    return mag * Math.cos(phase);
}

function getNormalRandom(mean, stddev) {
    return mean + boxMullerTransform() * stddev;
}

export function runMultiAttributeSimulation({ myBid, myTrueCost, myScores, competitors, priceWeight, numSimulations, auctionType }) {
    // Validate inputs
    if (!competitors || competitors.length === 0) {
        return { winRate: 0, averageWinningPayment: 0 };
    }
    
    let wins = 0;
    let totalWinningPayment = 0;
    const nonPriceWeight = 1 - (priceWeight / 100);
    const priceW = priceWeight / 100;

    const myNonPriceScore = (myScores.tech + myScores.exp) / 2;

    for (let i = 0; i < numSimulations; i++) {
        const allBidders = [{
            id: 'me',
            bid: myBid,
            nonPriceScore: myNonPriceScore
        }];

        competitors.forEach(compProfile => {
            for (let j = 0; j < compProfile.count; j++) {
                const compTrueCost = getNormalRandom(compProfile.costMean, compProfile.costStdDev);
                let compBid;
                if (auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH') {
                    // In procurement first-price auctions: bid above cost to ensure profit
                    // Apply markup based on competition level
                    const n_comp = competitors.reduce((sum, c) => sum + c.count, 0) + 1;
                    const markup = n_comp / (n_comp - 1); // More competition = lower markup
                    compBid = compTrueCost * markup;
                } else { 
                    // English/Vickrey: competitors apply Winner's Curse correction
                    const n_comp = competitors.reduce((sum, c) => sum + c.count, 0) + 1;
                    const competitorUncertainty = 10_000_000; // Competitors have some uncertainty awareness
                    compBid = compTrueCost + ((n_comp - 1) / (n_comp + 1)) * competitorUncertainty;
                }
                
                const compTechScore = getNormalRandom(85, 5);
                const compExpScore = getNormalRandom(88, 7);
                const compNonPriceScore = (compTechScore + compExpScore) / 2;
                
                allBidders.push({
                    id: `comp-${compProfile.type}-${j}`,
                    bid: compBid,
                    nonPriceScore: compNonPriceScore
                });
                
                // Debug logging for first few competitors
                if (i === 0 && j === 0) {
                    console.log(`${auctionType} - Competitor ${compProfile.type}: Cost ${compTrueCost.toFixed(0)}, Bid ${compBid.toFixed(0)}`);
                }
            }
        });

        const minBid = Math.min(...allBidders.map(b => b.bid));
        const maxBid = Math.max(...allBidders.map(b => b.bid));

        allBidders.forEach(bidder => {
            let priceScore = 0;
            if (maxBid > minBid) {
                priceScore = ((maxBid - bidder.bid) / (maxBid - minBid)) * 100;
            } else {
                priceScore = 100;
            }
            // Ensure scores are within valid ranges
            priceScore = Math.max(0, Math.min(100, priceScore));
            bidder.nonPriceScore = Math.max(0, Math.min(100, bidder.nonPriceScore));
            bidder.totalScore = (priceScore * priceW) + (bidder.nonPriceScore * nonPriceWeight);
        });

        allBidders.sort((a, b) => b.totalScore - a.totalScore);
        const winner = allBidders[0];

        if (winner.id === 'me') {
            wins++;
            if (auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH') {
                totalWinningPayment += myBid;
            } else { 
                const secondPlaceBid = allBidders[1].bid;
                totalWinningPayment += secondPlaceBid;
            }
        }
    }
    
    const winRate = wins / numSimulations;
    const averageWinningPayment = wins > 0 ? totalWinningPayment / wins : 0;

    return { winRate, averageWinningPayment };
} 